import React, { useState, useContext, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import { useHistory } from "react-router-dom";

import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import Pagination from "../../Pagination/Pagination";

// importing context
import { SearchContext } from "../../../context/states/search/searchState";
import { InterpreterContext } from "../../../context/states/interpreterState";
import AddInterpreter from "./interpreterForms/AddInterpreter";
import MemoizedList from "./memos/MemoizedList";
import MemoizedToolbar from "./memos/MemoizedToolbar";
import MemoizedHeader from "./memos/MemoizedHeader";

import { useStyles } from "./styles";

import { headCells } from "./HeadCells";

// ─── localStorage helpers ─────────────────────────────────────────────────────
const LS_KEY = "interpreterSearch";

const defaultSearchObj = {
  fname: "",
  language: "",
  dialect: "",
  gender: "",
  service: "",
  postcode: "",
  active: "",
  page: 1,
  limit: 50,
};

const loadSearchState = () => {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (stored) return JSON.parse(stored);
  } catch (_) {}
  return null;
};

const saveSearchState = (obj) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(obj));
  } catch (_) {}
};
// ─────────────────────────────────────────────────────────────────────────────

export const Interpreters = React.memo(({ title }) => {
  const classes = useStyles();
  const matches = useMediaQuery("(min-width:1920px)");
  const history = useHistory();

  const { getInterpretersHandler, loading, data, total, count, pagination } =
    useContext(InterpreterContext);
  const { interpreterSearchHandler, interpreterSearch } =
    useContext(SearchContext);

  // Initialise from localStorage first, then context, then hard defaults
  const [isSearchObj, setIsSearchObj] = useState(() => {
    const stored = loadSearchState();
    if (stored) return stored;
    return {
      fname: interpreterSearch?.fname ?? "",
      language: interpreterSearch?.language ?? "",
      dialect: interpreterSearch?.dialect ?? "",
      gender: interpreterSearch?.gender ?? "",
      service: interpreterSearch?.service ?? "",
      postcode: interpreterSearch?.postcode ?? "",
      active: interpreterSearch?.active ?? "",
      page: interpreterSearch?.page ?? 1,
      limit: interpreterSearch?.limit ?? 50,
    };
  });

  const [isCheckbox, setIsCheckbox] = useState(false);
  const enableCheckboxHandler = () => setIsCheckbox(!isCheckbox);

  const [isAddModel, setIsAddModel] = useState(false);
  const openFormModalHandler = useCallback(() => setIsAddModel(true), []);
  const closeFormModalHandler = useCallback(() => setIsAddModel(false), []);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("pincode");
  const [selected, setSelected] = useState([]);
  const [isPage, setPage] = useState(isSearchObj.page ?? 1);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(isSearchObj.limit ?? 50);

  // Persist search state to localStorage whenever it changes
  useEffect(() => {
    saveSearchState(isSearchObj);
    setPage(isSearchObj.page ?? 1);
    setRowsPerPage(isSearchObj.limit ?? 50);
  }, [isSearchObj]);

  // Clear localStorage when navigating away to a different top-level section
  useEffect(() => {
    const section = history.location.pathname.split("/")[1] || "";
    const unlisten = history.listen((nextLocation) => {
      const nextSection = nextLocation.pathname.split("/")[1] || "";
      if (nextSection !== section) {
        localStorage.removeItem(LS_KEY);
      }
    });
    return unlisten;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch interpreters whenever the search object changes
  useEffect(() => {
    let unmounted = false;
    const queryParams = interpreterSearchHandler(isSearchObj);
    if (!unmounted) {
      getInterpretersHandler(queryParams);
    }
    return () => {
      unmounted = true;
    };
  }, [isSearchObj]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAdvancedSearchHandler = useCallback(() => {
    const cleared = { ...defaultSearchObj };
    setIsSearchObj(cleared);
    setPage(1);
    setRowsPerPage(50);
    saveSearchState(cleared);
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(!isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n.email);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = useCallback(
    (event, name) => {
      const selectedIndex = selected.indexOf(name);
      let newSelected = [];
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }

      console.log("select: ", newSelected);
      setSelected(newSelected);
    },
    [setSelected, selected],
  );

  const handleChangePage = useCallback(
    (event, newPage) => {
      // newPage from MUI TablePagination is 0-indexed; API expects 1-indexed
      setIsSearchObj((prev) => ({
        ...prev,
        page: newPage + 1,
        limit: rowsPerPage,
      }));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value));
    const newLimit = parseInt(event.target.value, 10);

    if (!isNaN(newLimit)) {
      setIsSearchObj((prevState) => ({
        ...prevState,
        page: 1,
        limit: newLimit,
      }));
    }
  }, []);

  const handleChangeDense = useCallback(
    (event) => {
      setDense(event.target.checked);
    },
    [setDense],
  );

  const isSelected = useCallback(
    (name) => selected.indexOf(name) !== -1,
    [selected],
  );

  return (
    <>
      <Paper className={classes.paper}>
        <MemoizedToolbar
          selected={selected}
          title={title}
          isCheckbox={isCheckbox}
          enableCheckboxHandler={enableCheckboxHandler}
          openFormModalHandler={openFormModalHandler}
          count={count}
          isSearchObj={isSearchObj}
          setIsSearchObj={setIsSearchObj}
          setPage={setPage}
          isPage={isPage}
          clearAdvancedSearchHandler={clearAdvancedSearchHandler}
        />
        <TableContainer
          className={matches ? classes.container2 : classes.container}
        >
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            stickyHeader
            aria-label="sticky table"
          >
            <MemoizedHeader
              headCells={headCells}
              classes={classes}
              selected={selected}
              order={order}
              orderBy={orderBy}
              handleSelectAllClick={handleSelectAllClick}
              handleRequestSort={handleRequestSort}
              data={data}
              isCheckbox={isCheckbox}
            />
            <TableBody>
              <MemoizedList
                data={data}
                loading={loading}
                order={order}
                orderBy={orderBy}
                isPage={isPage}
                rowsPerPage={rowsPerPage}
                isSelected={isSelected}
                handleClick={handleClick}
                isCheckbox={isCheckbox}
              />
            </TableBody>
          </Table>
        </TableContainer>
        {Object.keys(pagination).length !== 0 && (
          <Pagination
            isTotal={total}
            isPage={isPage - 1}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense"
      />
      <AddInterpreter open={isAddModel} handleClose={closeFormModalHandler} />
    </>
  );
});

Interpreters.propTypes = {
  title: PropTypes.string.isRequired,
};
